import React, { ReactNode } from "react";
import { cn, TextContainer } from "../../lib/src";
import * as scss from "./docs-inline-message.scss";

type InlineMessageVariant =
	| "info"
	| "warning"
	| "best-practices"
	| "do-not-use-when"
	| "accessibility"
	| "writing";

export function InlineMessage(props: {
	children: ReactNode;
	variant?: InlineMessageVariant;
}): JSX.Element {
	const variant = variantToIconAndClassName(props.variant ?? "info");

	const children =
		React.Children.count(props.children) === 1 && typeof props.children === "string" ? (
			<p>{props.children}</p>
		) : (
			props.children
		);

	return (
		<div className={cn(scss.inlineMessage, variant.class)}>
			<div className={cn(scss.iconContainer, variant.class)}>{variant.icon}</div>
			<div className={scss.contentContainer}>
				<TextContainer article>{children}</TextContainer>
			</div>
		</div>
	);
}

function variantToIconAndClassName(variant: InlineMessageVariant): {
	icon: JSX.Element;
	class: string;
} {
	switch (variant) {
		case "info":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M11,7 L13,7 L13,9 L11,9 L11,7 Z M11,11 L13,11 L13,17 L11,17 L11,11 Z M11.99,2 C6.47,2 2,6.48 2,12 C2,17.52 6.47,22 11.99,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 11.99,2 Z M12,20 C7.58,20 4,16.42 4,12 C4,7.58 7.58,4 12,4 C16.42,4 20,7.58 20,12 C20,16.42 16.42,20 12,20 Z" />
					</svg>
				),
				class: scss.info,
			};
		case "warning":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M12,3.0292969 C11.436813,3.0292969 10.873869,3.2917399 10.558594,3.8164062 L1.7617188,18.451172 C1.1134854,19.529186 1.94287,21 3.2011719,21 L20.796875,21 C22.054805,21 22.886515,19.529186 22.238281,18.451172 L13.441406,3.8164062 C13.126131,3.29174 12.563187,3.0292969 12,3.0292969 Z M12,5.2988281 L20.236328,19 L3.7636719,19 L12,5.2988281 Z M11,9 L11,14 L13,14 L13,9 L11,9 Z M11,16 L11,18 L13,18 L13,16 L11,16 Z" />
					</svg>
				),
				class: scss.warning,
			};
		case "best-practices":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M10,17 L5,12 L6.41,10.59 L10,14.17 L17.59,6.58 L19,8 L10,17 Z" />
					</svg>
				),
				class: scss.bestPractices,
			};
		case "do-not-use-when":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M13,17 L11,17 L11,15 L13,15 L13,17 Z M13,13 L11,13 L11,7 L13,7 L13,13 Z" />
					</svg>
				),
				class: scss.doNotUseWhen,
			};
		case "accessibility":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M16,11 C17.66,11 18.99,9.66 18.99,8 C18.99,6.34 17.66,5 16,5 C14.34,5 13,6.34 13,8 C13,9.66 14.34,11 16,11 Z M8,11 C9.66,11 10.99,9.66 10.99,8 C10.99,6.34 9.66,5 8,5 C6.34,5 5,6.34 5,8 C5,9.66 6.34,11 8,11 Z M8,13 C5.67,13 1,14.17 1,16.5 L1,19 L15,19 L15,16.5 C15,14.17 10.33,13 8,13 Z M16,13 C15.71,13 15.38,13.02 15.03,13.05 C16.19,13.89 17,15.02 17,16.5 L17,19 L23,19 L23,16.5 C23,14.17 18.33,13 16,13 Z" />
					</svg>
				),
				class: scss.accessibility,
			};
		case "writing":
			return {
				icon: (
					<svg viewBox="0 0 24 24" focusable="false">
						<path d="M4,16.6671296 L4,20 L7.33287043,20 L17.1626163,10.1702541 L13.8297459,6.8373837 L4,16.6671296 Z M19.7400361,7.59283433 C20.0866546,7.2462158 20.0866546,6.68629357 19.7400361,6.33967505 L17.660325,4.25996389 C17.3137064,3.91334537 16.7537842,3.91334537 16.4071657,4.25996389 L14.7807249,5.88640467 L18.1135953,9.2192751 L19.7400361,7.59283433 Z" />
					</svg>
				),
				class: scss.writing,
			};
	}
}
